<template>
  <div class="ui-catalog" data-testid="ui-catalog" :style="visionFilter">
    <!-- «See as»: the filters the vision simulation applies to the catalog (colorTest.js). -->
    <svg class="ui-catalog__filters" aria-hidden="true" focusable="false">
      <filter v-for="(matrix, name) in visionMatrices" :id="`bz-vision-${name}`" :key="name" color-interpolation-filters="linearRGB">
        <feColorMatrix type="matrix" :values="feMatrix(matrix)" />
      </filter>
      <filter id="bz-vision-glare" color-interpolation-filters="linearRGB">
        <feComponentTransfer>
          <feFuncR type="linear" slope="1" :intercept="glare" />
          <feFuncG type="linear" slope="1" :intercept="glare" />
          <feFuncB type="linear" slope="1" :intercept="glare" />
        </feComponentTransfer>
      </filter>
    </svg>
    <BzScreen :title="$t('message.uiCatalogTitle')" :back-label="$t('message.back')" back-testid="ui-catalog-back"
              scroll-testid="ui-catalog-scroll" @back="$emit('go-back')">
      <p class="ui-catalog__intro">{{ $t('message.uiCatalogIntro') }}</p>

      <!-- The typeface comparison (stage 1): the whole system in DM Sans — the decided face — or beside it
           another candidate or the device's own, until the app reloads. Comes out when Adrián says. The
           palette comparison (6-oct-2026) does the same with the colours: the Bronze palette, or today's. -->
      <BzListGroup :level="2">
        <BzListRow icon="palette" :label="$t('message.uiCatalogFont')" :hint="$t('message.uiCatalogFontHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect v-model="fontTest" :labelled-by="labelId" :described-by="hintId" :options="fontOptions" data-testid="ui-catalog-font" />
          </template>
        </BzListRow>
        <BzListRow icon="palette" :label="$t('message.uiCatalogPalette')" :hint="$t('message.uiCatalogPaletteHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect v-model="paletteTest" :labelled-by="labelId" :described-by="hintId" :options="paletteOptions" data-testid="ui-catalog-palette" />
          </template>
        </BzListRow>
        <BzListRow icon="visibility" :label="$t('message.uiCatalogVision')" :hint="$t('message.uiCatalogVisionHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect v-model="vision" :labelled-by="labelId" :described-by="hintId" :options="visionOptions" data-testid="ui-catalog-vision" />
          </template>
        </BzListRow>
      </BzListGroup>

      <!-- The order list's traffic light, as the cards would be drawn on the palette: each state with its
           colour, its icon and its word (never the colour alone, WCAG 1.4.1). Static: only how they look;
           the list itself moves to the system last (stage 7). -->
      <section class="ui-catalog__section" data-testid="ui-catalog-traffic-light">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogTrafficLight') }}</h2>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogTrafficLightHint') }}</p>
        <div class="ui-catalog__orders">
          <div v-for="card in trafficLight" :key="card.code" class="bz-order" :class="[`bz-order--${card.state}`, { 'is-rider': card.rider }]">
            <span v-if="card.rider || card.delay" class="bz-order__badges">
              <span v-if="card.rider" class="bz-order__badge bz-order__badge--rider">
                <BzIcon name="delivery_dining" />{{ $t('message.riderIsHere') }} · {{ $t('message.riderWaitMinutes', { minutes: 4 }) }}
              </span>
              <span v-if="card.delay" class="bz-order__badge bz-order__badge--warn">
                <BzIcon name="schedule" />{{ $t('message.minutesAgo', { minutes: 14 }) }}
              </span>
            </span>
            <span class="bz-order__code">{{ card.code }}</span>
            <span class="bz-order__state"><BzIcon :name="card.icon" />{{ $t(card.label) }}</span>
          </div>
        </div>
      </section>

      <!-- The Bronze palette's ramps, every step it has (src/ui/colors.css), and the brand's yellow beside
           the two states it could be taken for. -->
      <section class="ui-catalog__section" data-testid="ui-catalog-ramps">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogRamps') }}</h2>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogRampsHint') }}</p>
        <div v-for="ramp in ramps" :key="ramp.name" class="ui-catalog__ramp">
          <h3 class="ui-catalog__ramp-name">{{ $t(`message.uiCatalogRampNames.${ramp.name}`) }}</h3>
          <div class="ui-catalog__ramp-steps">
            <div v-for="s in ramp.steps" :key="s.step" class="ui-catalog__step" :class="{ 'is-given': given.includes(s.token) }"
                 :style="{ '--step': s.step }">
              <span class="ui-catalog__step-colour" :style="{ background: `var(${s.token})` }"></span>
              <span class="ui-catalog__step-number">{{ s.step }}</span>
              <code>{{ s.value }}</code>
            </div>
          </div>
        </div>

        <h3 class="ui-catalog__ramp-name">{{ $t('message.uiCatalogBrand') }}</h3>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogBrandHint') }}</p>
        <div class="ui-catalog__chips">
          <span class="bz-chip bz-chip--brand"><BzIcon name="campaign" />{{ $t('message.changelogTagNew') }}</span>
          <span class="bz-chip bz-chip--wrong-solid"><BzIcon name="error" />{{ $t('message.tagReview') }}</span>
          <span class="bz-chip bz-chip--warn-solid"><BzIcon name="schedule" />{{ $t('message.minutesAgo', { minutes: 14 }) }}</span>
        </div>
      </section>

      <section class="ui-catalog__section" data-testid="ui-catalog-colors">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogColors') }}</h2>
        <div v-for="group in colorGroups" :key="group.name" class="ui-catalog__swatches">
          <div v-for="token in group.tokens" :key="token" class="ui-catalog__swatch">
            <span class="ui-catalog__chip" :style="{ background: `var(${token})` }"></span>
            <code>{{ token }}</code>
          </div>
        </div>
      </section>

      <section class="ui-catalog__section" data-testid="ui-catalog-type">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogType') }}</h2>
        <div v-for="step in typeSteps" :key="step.size" class="ui-catalog__type"
             :style="{ fontSize: `var(${step.size})`, lineHeight: `var(${step.leading})`, letterSpacing: `var(${step.tracking})` }">
          <span>{{ $t('message.uiCatalogTypeSample') }}</span>
          <code>{{ step.size }}</code>
        </div>
      </section>

      <section class="ui-catalog__section" data-testid="ui-catalog-buttons">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogButtons') }}</h2>
        <div v-for="size in ['xl', 'lg', 'md', 'sm']" :key="size" class="ui-catalog__buttons">
          <BzButton :size="size" kind="primary" :label="$t('message.uiCatalogPrimary')" />
          <BzButton :size="size" kind="secondary" :label="$t('message.uiCatalogSecondary')" />
          <BzButton v-if="size !== 'xl'" :size="size" kind="outlined" :label="$t('message.uiCatalogOutlined')" />
          <BzButton v-if="size !== 'xl'" :size="size" kind="text" :label="$t('message.uiCatalogText')" />
        </div>
        <div class="ui-catalog__buttons">
          <BzButton size="md" kind="secondary" icon="check" :label="$t('message.uiCatalogWithIcon')" />
          <BzButton size="md" kind="danger" icon="logout" :label="$t('message.signOut')" />
          <BzButton kind="secondary" icon="arrow_back" :aria-label="$t('message.back')" />
          <BzButton kind="secondary" icon="close" :aria-label="$t('message.close')" />
          <BzButton kind="primary" icon="cloud_upload" :aria-label="$t('message.uploadQueue')" />
        </div>
      </section>

      <section class="ui-catalog__section" data-testid="ui-catalog-list">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogLists') }}</h2>
        <BzListGroup :title="$t('message.uiCatalogGroup')" :level="3">
          <BzListRow action icon="campaign" :label="$t('message.changelogTitle')" :hint="$t('message.changelogSettingsHint')" />
          <BzListRow for-id="ui-catalog-toggle-on" icon="swap_vert" :label="$t('message.flipVertically')" :hint="$t('message.flipVerticallyHint')">
            <template #control="{ labelId, hintId }">
              <BzToggle v-model="sampleToggleOn" input-id="ui-catalog-toggle-on" :labelled-by="labelId" :described-by="hintId" />
            </template>
          </BzListRow>
          <BzListRow for-id="ui-catalog-toggle-off" icon="hide_image" :label="$t('message.blackImageDetection')">
            <template #control="{ labelId }">
              <BzToggle v-model="sampleToggleOff" input-id="ui-catalog-toggle-off" :labelled-by="labelId" />
            </template>
          </BzListRow>
          <BzListRow icon="camera" :label="$t('message.cameraCaptureMethod')">
            <template #control="{ labelId }">
              <BzSelect v-model="sampleSelect" :labelled-by="labelId" :options="sampleOptions" />
            </template>
          </BzListRow>
          <BzListRow icon="exposure" :label="$t('message.cameraExposureComp')">
            <template #below="{ labelId }">
              <BzSlider v-model="sampleSlider" centered :labelled-by="labelId" :min="-3" :max="3" :step="0.1" />
            </template>
          </BzListRow>
        </BzListGroup>
        <BzListGroup :level="3">
          <BzListRow action tone="danger" icon="logout" :label="$t('message.signOut')" />
        </BzListGroup>
      </section>

      <!-- The states of norm 11 as tags (the order and help screens use them from stage 2): a pill in
           the state's colour, its words always there. Two forms (paleta-bronze.md, «Cómo se usa» 3): the
           container for a deliberate screen, the solid to read at a glance. Static: only how they look. -->
      <section class="ui-catalog__section" data-testid="ui-catalog-chips">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogChips') }}</h2>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogChipsHint') }}</p>
        <h3 class="ui-catalog__ramp-name">{{ $t('message.uiCatalogChipsContainer') }}</h3>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogChipsContainerHint') }}</p>
        <div class="ui-catalog__chips">
          <span class="bz-chip">{{ $t('message.changelogTagNew') }}</span>
          <span class="bz-chip bz-chip--ok"><BzIcon name="check" />{{ $t('message.tagCorrect') }}</span>
          <span class="bz-chip bz-chip--wrong">{{ $t('message.tagReview') }}</span>
          <span class="bz-chip bz-chip--warn"><BzIcon name="wifi_off" />{{ $t('message.tagOffline') }}</span>
          <span class="bz-chip bz-chip--rider"><BzIcon name="delivery_dining" />{{ $t('message.riderIsHere') }}</span>
          <span class="bz-chip bz-chip--pending"><BzIcon name="schedule" />{{ $t('message.analyzing') }}</span>
        </div>
        <h3 class="ui-catalog__ramp-name">{{ $t('message.uiCatalogChipsSolid') }}</h3>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogChipsSolidHint') }}</p>
        <div class="ui-catalog__chips">
          <span class="bz-chip bz-chip--wrong-solid"><BzIcon name="error" />{{ $t('message.tagReview') }}</span>
          <span class="bz-chip bz-chip--warn-solid"><BzIcon name="schedule" />{{ $t('message.minutesAgo', { minutes: 14 }) }}</span>
          <span class="bz-chip bz-chip--brand"><BzIcon name="campaign" />{{ $t('message.changelogTagNew') }}</span>
        </div>
      </section>

      <!-- The tile that help and the manual order share (stages 2 and 3), in its two shapes: stacked (an
           icon at the top, the words at the foot: a grid of choices) and in a row (the icon beside the words,
           for a list read at a metre, `--far` with the label one step up). The chosen one in the selection's
           blue with its edge. Static. -->
      <section class="ui-catalog__section" data-testid="ui-catalog-tiles">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogTiles') }}</h2>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogTilesHint') }}</p>
        <h3 class="ui-catalog__ramp-name">{{ $t('message.uiCatalogTileStacked') }}</h3>
        <div class="ui-catalog__tiles">
          <div class="bz-tile">
            <BzIcon name="photo_camera" class="bz-tile__icon" />
            <span class="bz-tile__label">{{ $t('message.helpFlowTopicCannotTakePhoto') }}</span>
          </div>
          <div class="bz-tile is-selected">
            <BzIcon name="delivery_dining" class="bz-tile__icon" />
            <span class="bz-tile__label">{{ $t('message.helpFlowTopicLeftWithoutPhoto') }}</span>
          </div>
        </div>
        <h3 class="ui-catalog__ramp-name">{{ $t('message.uiCatalogTileRow') }}</h3>
        <div class="ui-catalog__tiles ui-catalog__tiles--rows">
          <div class="bz-tile bz-tile--row">
            <BzIcon name="photo_camera" class="bz-tile__icon" />
            <span class="bz-tile__label">{{ $t('message.helpFlowTopicCannotTakePhoto') }}</span>
          </div>
          <div class="bz-tile bz-tile--row is-selected">
            <BzIcon name="delivery_dining" class="bz-tile__icon" />
            <span class="bz-tile__label">{{ $t('message.helpFlowTopicLeftWithoutPhoto') }}</span>
          </div>
        </div>
        <h3 class="ui-catalog__ramp-name">{{ $t('message.uiCatalogTileFar') }}</h3>
        <div class="ui-catalog__tiles ui-catalog__tiles--rows">
          <div class="bz-tile bz-tile--row bz-tile--far">
            <BzIcon name="photo_camera" class="bz-tile__icon" />
            <span class="bz-tile__label">{{ $t('message.helpFlowTopicCannotTakePhoto') }}</span>
          </div>
          <div class="bz-tile bz-tile--row bz-tile--far is-selected">
            <BzIcon name="delivery_dining" class="bz-tile__icon" />
            <span class="bz-tile__label">{{ $t('message.helpFlowTopicLeftWithoutPhoto') }}</span>
          </div>
        </div>
      </section>

      <section class="ui-catalog__section" data-testid="ui-catalog-dialog">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogDialog') }}</h2>
        <BzButton size="md" kind="secondary" :label="$t('message.uiCatalogOpenDialog')"
                  data-testid="ui-catalog-open-dialog" @click="dialogOpen = true" />
      </section>

      <!-- The toasts the critical lane will draw (design system stage 5), shown here to be approved first.
           Static: no timer, no swipe — only how they look. -->
      <section class="ui-catalog__section" data-testid="ui-catalog-toasts">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogToasts') }}</h2>
        <div class="ui-catalog__toasts">
          <div class="bz-toast bz-toast--rider">
            <span class="bz-toast__icon"><BzIcon name="delivery_dining" /></span>
            <span class="bz-toast__body">
              <span class="bz-toast__meta">{{ $t('message.riderArrivedTitle') }}</span>
              <span class="bz-toast__code">284910037</span>
            </span>
            <span class="bz-toast__aside"><BzIcon name="schedule" />{{ $t('message.riderWaitMinutes', { minutes: 3 }) }}</span>
          </div>
          <div class="bz-toast">
            <span class="bz-toast__icon"><BzIcon name="wifi_off" /></span>
            <span class="bz-toast__body">
              <span class="bz-toast__meta">{{ $t('message.tagOffline') }}</span>
              <span class="bz-toast__code bz-toast__code--sm">#045</span>
              <span class="bz-toast__text">{{ $t('message.bodyQueuedOffline') }}</span>
            </span>
          </div>
          <div class="bz-snackbar">
            <BzIcon name="check_circle" class="bz-snackbar__icon" />
            <span>{{ $t('message.coverageThanks') }}</span>
          </div>
        </div>
      </section>

      <!-- What a list says when it has nothing to show or cannot reach it: an icon in the second ink, what
           is happening and, if there is one, what to do. Still icons: a stopped state does not turn. -->
      <section class="ui-catalog__section" data-testid="ui-catalog-empty">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogEmpty') }}</h2>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogEmptyHint') }}</p>
        <div class="ui-catalog__frames">
          <div class="ui-catalog__frame">
            <div class="bz-empty">
              <BzIcon name="image" class="bz-empty__icon" />
              <p class="bz-empty__title">{{ $t('message.uiCatalogEmptyTitle') }}</p>
              <p class="bz-empty__text">{{ $t('message.uiCatalogEmptyText') }}</p>
            </div>
          </div>
          <div class="ui-catalog__frame">
            <div class="bz-empty">
              <BzIcon name="wifi_off" class="bz-empty__icon" />
              <p class="bz-empty__title">{{ $t('message.uiCatalogOfflineTitle') }}</p>
              <p class="bz-empty__text">{{ $t('message.uiCatalogOfflineText') }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Indeterminate progress: the icon turns while the app is still trying, and the words say what.
           With «remove animations» it holds still. -->
      <section class="ui-catalog__section" data-testid="ui-catalog-spinner">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogSpinner') }}</h2>
        <p class="ui-catalog__note">{{ $t('message.uiCatalogSpinnerHint') }}</p>
        <p class="ui-catalog__loading" role="status">
          <BzIcon name="progress_activity" class="bz-spinner" />{{ $t('message.uiCatalogLoading') }}
        </p>
      </section>

      <section class="ui-catalog__section" data-testid="ui-catalog-icons">
        <h2 class="ui-catalog__heading">{{ $t('message.uiCatalogIcons') }}</h2>
        <div class="ui-catalog__icons">
          <span v-for="name in iconNames" :key="name" class="ui-catalog__icon"><BzIcon :name="name" /><code>{{ name }}</code></span>
        </div>
      </section>
    </BzScreen>

    <BzDialog card :visible="dialogOpen" band="modal"
              :eyebrow="$t('message.uiCatalogDialogEyebrow')"
              :title="$t('message.uiCatalogDialogTitle')"
              :subtitle="$t('message.uiCatalogDialogSubtitle')"
              :close-label="$t('message.close')"
              mask-testid="ui-catalog-dialog-mask"
              @dismiss="dialogOpen = false">
      <p class="ui-catalog__dialog-body">{{ $t('message.uiCatalogDialogBody') }}</p>
      <template #footer>
        <div class="ui-catalog__dialog-actions">
          <BzButton size="lg" kind="secondary" :label="$t('message.uiCatalogSecondary')" @click="dialogOpen = false" />
          <BzButton size="lg" kind="primary" :label="$t('message.uiCatalogPrimary')" @click="dialogOpen = false" />
        </div>
      </template>
    </BzDialog>
  </div>
</template>

<script>
import BzButton from '../components/BzButton.vue';
import BzDialog from '../components/BzDialog.vue';
import BzIcon from '../components/BzIcon.vue';
import BzListGroup from '../components/BzListGroup.vue';
import BzListRow from '../components/BzListRow.vue';
import BzScreen from '../components/BzScreen.vue';
import BzSelect from '../components/BzSelect.vue';
import BzSlider from '../components/BzSlider.vue';
import BzToggle from '../components/BzToggle.vue';
import { ICONS } from '../icons';
import { applyPalette, currentPalette, feMatrix, GIVEN, GLARE, readRamps, VISION_MATRICES, VISIONS } from './colorTest';
import { applyFontTest, currentFontTest, TYPE_STEPS } from './fontTest';

/**
 * The design system, live: every token and component of src/ui/ as the tablet draws it, opened from
 * Settings → Debug. docs/sistema-diseno/README.md says what each one is for; this shows it.
 *
 * @rule   Only the library: nothing of the app's own components or state is imported here (src/ui/ can
 *         leave the app one day). The real notification on the system is opened from Debug instead.
 * @rule   A token or component that enters the system enters this page in the same change, and the
 *         visual harness photographs it (tests/visual/screens.visual.spec.js).
 * @why    In every build, imported statically: the Tracker never splits its bundle (a tablet with an old
 *         index.html would ask for chunks that no longer exist, docs/migracion-ui/referencia.md §3.1).
 */
export default {
  name: 'UiCatalog',
  components: { BzButton, BzDialog, BzIcon, BzListGroup, BzListRow, BzScreen, BzSelect, BzSlider, BzToggle },
  emits: ['go-back'],
  data() {
    return {
      dialogOpen: false,
      sampleToggleOn: true,
      sampleToggleOff: false,
      sampleSelect: 'auto',
      sampleSlider: 0.6,
      fontTest: currentFontTest(),
      paletteTest: currentPalette(),
      vision: 'normal',
      visionMatrices: VISION_MATRICES,
      glare: GLARE,
      ramps: readRamps(),
      given: GIVEN,
      // The traffic light's cards: the four states a card can be in, and an order to photograph with a
      // rider waiting and with a delay, the two things laid over a card.
      trafficLight: [
        { state: 'todo', icon: 'photo_camera', label: 'message.uiCatalogStateTodo', code: '#045' },
        { state: 'ok', icon: 'check_circle', label: 'message.tagCorrect', code: '#046' },
        { state: 'wrong', icon: 'error', label: 'message.tagReview', code: '#047' },
        { state: 'pending', icon: 'cloud_upload', label: 'message.uiCatalogStatePending', code: '#048' },
        { state: 'todo', icon: 'photo_camera', label: 'message.uiCatalogStateTodo', code: '#049', rider: true },
        { state: 'todo', icon: 'photo_camera', label: 'message.uiCatalogStateTodo', code: '#050', delay: true },
      ],
      // Every colour of the system, in the order of the README's tables.
      colorGroups: [
        { name: 'surfaces', tokens: ['--bz-bg', '--bz-surface', '--bz-raised', '--bz-control', '--bz-edge'] },
        { name: 'ink', tokens: ['--bz-ink', '--bz-ink-2', '--bz-outline'] },
        { name: 'action', tokens: ['--bz-action', '--bz-on-action', '--bz-action-ink', '--bz-action-container', '--bz-focus', '--bz-handle', '--bz-track-active'] },
        { name: 'todo', tokens: ['--bz-todo-solid', '--bz-on-todo-solid'] },
        { name: 'ok', tokens: ['--bz-ok', '--bz-ok-solid', '--bz-on-ok-solid', '--bz-ok-container', '--bz-on-ok-container'] },
        { name: 'wrong', tokens: ['--bz-wrong', '--bz-wrong-solid', '--bz-on-wrong-solid', '--bz-wrong-container', '--bz-on-wrong-container'] },
        { name: 'warn', tokens: ['--bz-warn', '--bz-warn-solid', '--bz-on-warn-solid', '--bz-warn-container', '--bz-on-warn-container'] },
        { name: 'rider', tokens: ['--bz-rider', '--bz-rider-solid', '--bz-on-rider-solid', '--bz-rider-border', '--bz-rider-container', '--bz-on-rider-container'] },
        { name: 'pending', tokens: ['--bz-pending', '--bz-pending-solid', '--bz-on-pending-solid', '--bz-pending-container', '--bz-on-pending-container'] },
        { name: 'brand', tokens: ['--bz-brand', '--bz-on-brand'] },
        { name: 'overlay', tokens: ['--bz-danger', '--bz-scrim', '--bz-disabled-container', '--bz-disabled-ink'] },
      ],
      typeSteps: [...TYPE_STEPS].reverse().map((s) => ({ size: `--bz-text-${s}`, leading: `--bz-leading-${s}`, tracking: `--bz-tracking-${s}` })),
      iconNames: Object.keys(ICONS),
    };
  },
  methods: {
    feMatrix,
  },
  watch: {
    fontTest(choice) {
      applyFontTest(choice);
    },
    paletteTest(choice) {
      applyPalette(choice);
    },
  },
  computed: {
    visionFilter() {
      return this.vision === 'normal' ? null : { filter: `url(#bz-vision-${this.vision})` };
    },
    visionOptions() {
      return VISIONS.map((value) => ({ value, label: this.$t(`message.uiCatalogVisionNames.${value}`) }));
    },
    fontOptions() {
      return [
        { value: 'dmsans', label: this.$t('message.uiCatalogFontBrand'), short: 'DM Sans' },
        { value: 'instrument', label: 'Instrument Sans' },
        { value: 'schibsted', label: 'Schibsted Grotesk' },
        { value: 'system', label: this.$t('message.uiCatalogFontSystem') },
      ];
    },
    paletteOptions() {
      return [
        { value: 'bronze', label: this.$t('message.uiCatalogPaletteBronze'), short: 'Bronze' },
        { value: 'current', label: this.$t('message.uiCatalogPaletteCurrent') },
      ];
    },
    sampleOptions() {
      return [
        { value: 'auto', label: this.$t('message.cameraCaptureMethodCanvas') },
        { value: 'imageCapture', label: this.$t('message.cameraCaptureMethodImageCapture') },
      ];
    },
  },
};
</script>

<style scoped>
.ui-catalog {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.ui-catalog__intro {
  margin: 0 0 var(--bz-space-7);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  color: var(--bz-ink-2);
}

.ui-catalog__section {
  margin-bottom: var(--bz-space-8);
}

.ui-catalog__heading {
  margin: 0 0 var(--bz-space-4);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-label);
}

.ui-catalog__note {
  margin: 0 0 var(--bz-space-4);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  color: var(--bz-ink-2);
}

/* ---- The ramps: each step a colour, its number and its value; the two given colours ringed. ---- */
.ui-catalog__ramp {
  margin-bottom: var(--bz-space-6);
}

.ui-catalog__ramp-name {
  margin: 0 0 var(--bz-space-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-title);
}

/* Twelve columns, each step in its own: a step a ramp does not have leaves its gap, so step 9 of every
   ramp stands in one column and the lightness they share reads down it. Narrower than a tablet lying down,
   a value no longer fits its column (it needs 59 px), so they wrap instead. */
.ui-catalog__ramp-steps {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--bz-space-2);
}

.ui-catalog__step {
  grid-column: var(--step);
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-1);
  min-width: 0;
}

@media (max-width: 960px) {
  .ui-catalog__ramp-steps {
    grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  }

  .ui-catalog__step {
    grid-column: auto;
  }
}

.ui-catalog__step-colour {
  height: var(--bz-target-min);
  border-radius: var(--bz-radius-sm);
  box-shadow: inset 0 0 0 1px var(--bz-edge);
}

.ui-catalog__step.is-given .ui-catalog__step-colour {
  box-shadow: inset 0 0 0 2px var(--bz-ink);
}

.ui-catalog__step-number {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-title);
}

.ui-catalog__swatches {
  display: flex;
  flex-wrap: wrap;
  gap: var(--bz-space-2);
  margin-bottom: var(--bz-space-3);
}

.ui-catalog__swatch {
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  padding: var(--bz-space-2) var(--bz-space-3) var(--bz-space-2) var(--bz-space-2);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-full);
  background: var(--bz-surface);
}

.ui-catalog__chip {
  width: 32px;
  height: 32px;
  border-radius: var(--bz-radius-full);
  box-shadow: inset 0 0 0 1px var(--bz-edge);
}

.ui-catalog code {
  font-size: var(--bz-text-sm);
  letter-spacing: var(--bz-tracking-sm);
  color: var(--bz-ink-2);
  white-space: nowrap;
}

.ui-catalog__type {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--bz-space-4);
  padding: var(--bz-space-2) 0;
  font-weight: var(--bz-weight-label);
}

.ui-catalog__buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--bz-target-gap);
  margin-bottom: var(--bz-space-4);
}

.ui-catalog__dialog-body {
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  color: var(--bz-ink-2);
}

.ui-catalog__dialog-actions {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: var(--bz-target-gap);
  width: 100%;
}

/* Each icon as wide as its name, as the colour swatches: a fixed column cuts the long ones. */
.ui-catalog__icons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--bz-space-2);
}

.ui-catalog__icon {
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  padding: var(--bz-space-2) var(--bz-space-3);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-md);
  background: var(--bz-surface);
  font-size: var(--bz-icon-md);
  color: var(--bz-ink);
}

/* ---- Tags: the states of norm 11 in a pill (stage 2 on). ---- */
.ui-catalog__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--bz-space-2);
}

/* ---- The traffic light: a card per state, its fill luminous and its ink dark — the order list's look,
   on the palette. The words and the icon say the state; the colour finds it at a glance. ---- */
.ui-catalog__filters {
  position: absolute;
  width: 0;
  height: 0;
}

.ui-catalog__orders {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--bz-space-6) var(--bz-target-gap);
}

.bz-order {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: var(--bz-space-2);
  min-height: 152px;
  box-sizing: border-box;
  padding: var(--bz-space-4);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-todo-solid);
  color: var(--bz-on-todo-solid);
}

.bz-order--ok { background: var(--bz-ok-solid); color: var(--bz-on-ok-solid); }
.bz-order--wrong { background: var(--bz-wrong-solid); color: var(--bz-on-wrong-solid); }
.bz-order--pending { background: var(--bz-pending-solid); color: var(--bz-on-pending-solid); }

/* A rider waiting: a ring in the rider's red drawn on the page, 2 px off the card, so it reads around a
   card of any colour (on the card itself, a red edge beside an orange fill would not). */
.bz-order.is-rider {
  outline: 3px solid var(--bz-rider-border);
  outline-offset: 2px;
}

.bz-order__code {
  margin-top: auto;
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
  font-weight: var(--bz-weight-strong);
  font-variant-numeric: tabular-nums;
}

.bz-order__state {
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-label);
}

.bz-order__state :deep(.bz-icon) {
  flex: 0 0 auto;
  font-size: var(--bz-icon-md);
}

.bz-order__badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--bz-space-2);
}

/* What is laid over a card wears a 2 px cut of the background around it, so it holds its shape on a fill
   of any colour — the rider's red on an orange card, the delay's yellow on a green one. */
.bz-order__badge {
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-1);
  min-height: 28px;
  box-sizing: border-box;
  padding: 0 var(--bz-space-2);
  border-radius: var(--bz-radius-full);
  box-shadow: 0 0 0 2px var(--bz-bg);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
  white-space: nowrap;
}

.bz-order__badge :deep(.bz-icon) {
  font-size: var(--bz-icon-xs);
}

.bz-order__badge--rider { background: var(--bz-rider-solid); color: var(--bz-on-rider-solid); }
.bz-order__badge--warn { background: var(--bz-warn-solid); color: var(--bz-on-warn-solid); }

/* ---- The tile (stages 2 and 3): an icon at the top, the words at the foot, on the system's card. ---- */
.ui-catalog__tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--bz-target-gap);
  margin-bottom: var(--bz-space-6);
}

/* The row tile is wider: its icon and its words side by side. */
.ui-catalog__tiles--rows {
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}

/* ---- Empty and offline: each in the space a list would take, on the page's surface. ---- */
.ui-catalog__frames {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--bz-target-gap);
}

.ui-catalog__frame {
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

/* ---- The spinner beside its words. ---- */
.ui-catalog__loading {
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-2);
  margin: 0;
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
}

.ui-catalog__loading :deep(.bz-icon) {
  font-size: var(--bz-icon-md);
}

/* ---- The toasts (design system stage 5): a card that floats — the raised surface, its edge and a
   short shadow; the rider's in its own red, so it is never mistaken for a notice. 440 px wide, the
   production rider toast's. ---- */
.ui-catalog__toasts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--bz-space-3);
}

</style>
